import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { z } from "zod";

export const Theme = {
  Light: "light",
  Dark: "dark",
} as const;

export const themeSchema = z.enum(Theme);
export type Theme = z.infer<typeof themeSchema>;

interface ThemeContextValue {
  theme: Theme;
  isDark: boolean;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);
const THEME_KEY = "avizo.theme";

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    const stored = localStorage.getItem(THEME_KEY);
    const parsed = themeSchema.safeParse(stored);
    return parsed.success ? parsed.data : Theme.Light;
  });

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === Theme.Dark);
    localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, isDark: theme === Theme.Dark, setTheme: setThemeState }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme deve ser usado dentro de ThemeProvider");
  }
  return context;
}
